<template>
  <data-table
    :key="key"
    :action-options="actionOptions"
    :data="rows"
    :disable-set-query-string="disableSetQueryString"
    :headers="computedHeaders"
    :hide-delete="hideDelete"
    :hide-edit="hideEdit"
    :hide-table-bottom="hideTableBottom"
    :loading="loading"
    :table-name="tableName"
    :total-items="totalItens"
    :has-totals="hasTotals"
    :list-property-name="listPropertyName"
    :selected-rows="selectedRows"
    :select="selectRows"
    :select-list-filter="selectList"
    :show-expand="showExpand"
    :expand-component="expandComponent"
    :bg-extension="bgExpand"
    :context-menu="contextMenu"
    @delete="deleteAction($event)"
    @edit="editAction($event)"
    @update-order="orderUpdated($event)"
    @update-page="pageUpdated($event)"
    @update-rows-per-page="rowsPerPageUpdated($event)"
    @select="selectedAllRows($event)"
    @select-all="selectedAll($event)"
    @totals-select="totalSelected($event)"
    @updated-page-by-table="refreshPage($event)"
  />
</template>

<script>
import DataTable from 'Arch/components/data-table/DataTable.vue';
import DataTableService from 'Arch/components/data-table/DatatableService';
import NotificationMixin from 'Arch/mixins/NotificationMixin';
import QueryStringMixin from 'Arch/components/crud/crud-mixins/QueryStringMixin';
import RouterMixin from 'Arch/mixins/RouterMixin';
import UseSwrMixin from 'Arch/mixins/useSwrMixin';

export default {
  name: 'CrudList',
  components: {
    DataTable,
  },
  mixins: [RouterMixin, QueryStringMixin, NotificationMixin, UseSwrMixin],
  data: (vm) => ({
    disableSetQueryString: false,
    loading: false,
    hideEdit: false,
    hideDelete: false,
    selectRows: false,
    hideTableBottom: false,
    tableName: vm.$options.name,
    deleteConfirmTextMessage: 'Deseja remover este registro?',
    deleteConfirmTitleMessage: 'Excluir registro',
    deleteErrorTextMessage:
      'Não foi possível remover este registro no momento. Por favor, tente novamente mais tarde.',
    deleteErrorTitleMessage: 'Ocorreu um erro!',
    deleteSuccessTextMessage: 'Registro removido com sucesso.',
    deleteSuccessTitleMessage: 'Registro excluido.',
    actionOptions: [],
    requestClass: null,
    headers: null,
    overrideHeaders: null,
    extraHeaders: null,
    preHeaders: null,
    key: null,
    hasTotals: false,
    listPropertyName: null,
    selectList: [],
    isTecfin: false,
    showExpand: false,
    expandComponent: {},
    showContextMenu: true,
    contextMenuItems: [],
    listKey: vm.$options.name,
    bgExpand: '',
  }),
  computed: {
    contextMenu() {
      return {
        show: this.showContextMenu,
        items: this.contextMenuItems,
      };
    },
    computedHeaders() {
      return this.prepareOverrideHeaders(
        this.headers,
        this.overrideHeaders,
      );
    },
    prepareOverrideHeaders() {
      return (headers, overrides) => {
        if (Array.isArray(headers)) {
          return [
            ...(Array.isArray(this.preHeaders) ? this.preHeaders : []),
            ...headers.map(
              (header, i) => ({
                ...header,
                key: `header_${i}`,
              }),
            ),
            ...(Array.isArray(this.extraHeaders) ? this.extraHeaders : []),
          ].map(
            (header) => this.prepareHeaderOverride(header, overrides),
          );
        }
        return headers;
      };
    },
    prepareHeaderOverride() {
      return (header, overrides) => {
        if (Array.isArray(overrides)) {
          return this.prepareArrayHeaderOverride(header, overrides);
        }
        if (typeof overrides === 'function') {
          return this.prepareFunctionHeaderOverride(header, overrides);
        }
        return header;
      };
    },
    prepareArrayHeaderOverride() {
      return (header, overrides) => {
        const override = overrides.find(
          (o) => {
            if (o.key) {
              return o.key === header.key;
            }
            return o.value === header.value;
          },
        );
        if (override) {
          return {
            ...header,
            ...override,
          };
        }
        return header;
      };
    },
    prepareFunctionHeaderOverride() {
      return (header, overrides) => overrides(header);
    },
    getSwrKey() {
      return `swr:crudlist-${this.listKey}:${this.requestClass.url()}`;
    },
  },
  watch: {
    totalItens: {
      handler(value) {
        this.$root.$emit(`total-itens-${this.tableName}`, value);
      },
    },
    rows: {
      handler(value) {
        if (value) {
          this.selectedRows = this.rows.map((row) => ({
            value: false,
            row,
          }));
        }
      },
    },
  },
  mounted() {
    this.checkForAttributes();
  },
  created() {
    this.refleshCrud();
    if (this.showExpand && !this.expandComponent) {
      throw new Error('É necessário um componente para expansão');
    }
  },
  provide() {
    return {
      rows: this.rows,
      setRows: (rows) => {
        this.setNewRows(rows);
      },
      editListAction: (item) => this.editAction(item),
      removeListAction: (item) => this.deleteAction(item),
      refreshCrudList: this.refreshPage,
    };
  },
  beforeCreate() {
    const filters = DataTableService.getFilters(this.$options.name);
    if (Object.keys(this.$route.query).length === 0 && filters !== null) {
      this.$router.push({ query: filters });
    }
  },
  methods: {
    refreshPage() {
      this.getData();
    },
    setNewRows(rows) {
      this.rows = rows;
    },
    checkForAttributes() {
      if (this.headers === null) {
        throw new Error(`Headers do component: ${this.$options.name} não especificados`);
      }
      if (this.requestClass === null) {
        throw new Error(
          `Classe de request não especificada para o componente: ${this.$options.name}`,
        );
      }
      if (!this.hideEdit && !this.editRoute) {
        throw new Error('Rota de edição não definida');
      }
      return true;
    },
    getData() {
      this.makeRequest();
    },
    makeRequest() {
      this.makeRestRequest();
    },
    makeRestRequest() {
      const params = this.getQueryParams();
      this.loading = true;
      this.emitLoadingList();
      this.useSwr(
        `${this.getSwrKey}/${
          params ? JSON.stringify(params) : 'page=1'
        }`,
        () => this.prepareRequest(params),
        () => {
          this.finalListRequest();
        },
      );
    },
    finalListRequest() {
      this.prepareLoadings();
    },
    prepareLoadings() {
      this.loading = false;
      this.emitLoadingList();
    },
    emitLoadingList() {
      this.$root.$emit(`loading-crud-${this.tableName}`, this.loading);
    },
    prepareRequest(params) {
      return this.requestClass.list(params);
    },
    whenSwrResponse(response, error) {
      if (error) {
        this.showErrorToast({
          title: 'Erro na listagem!',
          text: this.getMessageError(error),
        });
      } else {
        this.handleResponse(response);
      }
    },
    getMessageError(error) {
      if (!error) {
        return 'Ocorreu um erro ao buscar os dados';
      }
      if (!error.response) {
        return error.message;
      }
      if (!error.response.data) {
        return error.response.statusText;
      }
      return error.response.data.message;
    },
    handleResponse(response) {
      this.setRowsAndTotalItens(
        this.getResponseRows(response),
        this.getResponseTotalRows(response),
      );
    },
    getResponseRows(response) {
      if (this.hasTotals) {
        this.$root.$emit('totals', response);
      }
      if (response.data) {
        if (response.data.content) {
          const { content } = response.data;
          return this.returnToArray(content);
        }
        if (response.data.data) {
          const { data } = response.data;
          return this.returnToArray(data);
        }
        if (this.listPropertyName) {
          return this.returnToArray(response.data[this.listPropertyName]);
        }
      }
      return this.returnToArray(response.data);
    },
    returnToArray(value) {
      return Array.isArray(value)
        ? this.prepareResponseData(value)
        : this.prepareResponseData([value]);
    },
    prepareResponseData(data) {
      return data;
    },
    getResponseTotalRows(response) {
      if (response && response.data) {
        const { data } = response;
        return this.isTecfin
          ? data.totalElements
          : data.total;
      }
      return 0;
    },
    setRowsAndTotalItens(rows, total) {
      this.totalItens = total;
      this.rows.splice(0);
      if (rows && rows.length) {
        this.rows.push(...rows.map((item) => this.mapRequestItem(item)));
      } else {
        this.rows.push();
      }
    },
    prepareItemRow(item) {
      return item;
    },
    editAction(item) {
      this.navigateTo(this.editRoute, { id: item.id }).catch(() => {});
    },
    goTo(...args) {
      this.navigateTo(...args).catch(() => {});
    },
    deleteAction(row) {
      this.showConfirmDeleteDialog({
        title: this.deleteConfirmTitleMessage,
        text: this.deleteConfirmTextMessage,
      }).then((data) => {
        if (data.value) {
          this.makeDelete(row)
            .then(() => {
              this.removeFromListAfterDelete(row);
              this.deleteSuccess(data);
            })
            .catch((error) => {
              this.deleteError(error);
            })
            .finally(() => {
              this.getData();
            });
        }
      });
    },
    removeFromListAfterDelete(row) {
      const indexDeleted = this.rows.indexOf(row);
      this.rows.splice(indexDeleted, 1);
    },
    makeDelete(row) {
      return this.makeRestDelete(row);
    },
    makeRestDelete(row) {
      return this.requestClass.remove(row.id);
    },
    deleteSuccess() {
      this.showSuccessToast({
        title: 'Sucesso!',
        text: this.deleteSuccessTextMessage,
      });
    },
    deleteError() {
      this.showErrorToast({
        title: this.deleteErrorTitleMessage,
        text: this.deleteErrorTextMessage,
      });
    },
    mapRequestItem(item) {
      if (typeof this.mapItemMethod !== 'undefined') {
        return this.mapItemMethod(item);
      }

      return item;
    },
    refleshCrud() {
      this.$root.$on(`refresh-crud-${this.tableName}`, () => {
        this.getData();
      });
    },
    selectedAllRows(event) {
      this.$root.$emit('selected-all-rows', event);
    },
    selectedAll(value) {
      this.$root.$emit('selected-all', value);
    },
    totalSelected(value) {
      this.$root.$emit('total-selected', value);
    },
    getOnRemove() {
      this.$root.$on('remove-select-rows', (rows) => {
        this.makeDeleteSelectRows(rows);
      });
    },
    updateSelectRows() {
      this.$root.$on('update-selected-all-rows', (rows = []) => {
        if (this.selectedRows) {
          this.selectedRows = rows;
        }
      });
    },
    makeDeleteSelectRows(rows) {
      this.loading = true;
      Promise.all(this.makeDeletePromises(rows))
        .catch(this.errorSelectCallBack)
        .then(this.successSelectCallBack)
        .finally(() => {
          this.makeRequest();
        });
    },
    makeDeletePromises(rows) {
      return rows.map((row) => this.requestClass.remove(row.id));
    },
    errorSelectCallBack({ response }) {
      this.showErrorToast({
        text: response.data.message || 'Falha ao excluir items',
      });
    },
    successSelectCallBack() {
      this.showSuccessToast({
        text: 'Sucesso ao excluir items',
      });
    },
  },
};
</script>
